import clsx from "clsx";
import { BarChart3, RefreshCw } from "lucide-react";
import { useState } from "react";

import { useUsageStats } from "@/hooks/useUsageStats";

import { formatInt } from "./stats/formatters";
import { StatsAdvanced } from "./stats/StatsAdvanced";
import { StatsOverview } from "./stats/StatsOverview";

type StatsTab = "overview" | "advanced";

export function UsageStatsView(): JSX.Element {
  const [days, setDays] = useState(14);
  const [mine, setMine] = useState(true);
  const [tab, setTab] = useState<StatsTab>("overview");
  const { data, isLoading, isFetching, refetch } = useUsageStats(days, mine);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-cyber-border px-2 py-2 md:px-4 md:py-3">
        <div className="flex flex-wrap items-center justify-between gap-2 px-2 md:gap-3 md:px-6">
          <div className="flex items-center gap-2">
            <BarChart3 size={16} className="text-cyber-cyan" />
            <h2 className="text-sm font-medium text-cyber-text">Usage stats</h2>
            {data && (
              <span className="rounded-full bg-cyber-surface px-1.5 py-0.5 text-[10px] text-cyber-muted">
                {formatInt(data.totals.responses)} responses
              </span>
            )}
          </div>

          <div className="flex items-center gap-1.5 md:gap-2">
            {/* Tab toggle */}
            <div className="flex items-center overflow-hidden rounded border border-cyber-border">
              {(["overview", "advanced"] as const).map((value) => (
                <button
                  key={value}
                  onClick={() => setTab(value)}
                  className={clsx(
                    "px-2 py-1 text-xs capitalize transition-colors",
                    tab === value
                      ? "bg-cyber-cyan/10 text-cyber-cyan"
                      : "text-cyber-muted hover:bg-cyber-surface/50 hover:text-cyber-text",
                  )}
                >
                  {value}
                </button>
              ))}
            </div>

            {/* Time window toggle */}
            <div className="flex items-center overflow-hidden rounded border border-cyber-border">
              {[7, 14, 30].map((value) => (
                <button
                  key={value}
                  onClick={() => setDays(value)}
                  className={clsx(
                    "px-2 py-1 text-xs transition-colors",
                    days === value
                      ? "bg-cyber-cyan/10 text-cyber-cyan"
                      : "text-cyber-muted hover:bg-cyber-surface/50 hover:text-cyber-text",
                  )}
                >
                  {value}d
                </button>
              ))}
            </div>

            <button
              onClick={() => setMine((current) => !current)}
              className={clsx(
                "rounded border px-2.5 py-1 text-xs transition-colors",
                mine
                  ? "border-cyber-cyan/40 bg-cyber-cyan/10 text-cyber-cyan"
                  : "border-cyber-border text-cyber-muted hover:bg-cyber-surface/50 hover:text-cyber-text",
              )}
            >
              {mine ? "Mine" : "All users"}
            </button>

            <button
              onClick={() => refetch()}
              className="rounded p-1.5 text-cyber-muted transition-colors hover:bg-cyber-surface/50 hover:text-cyber-cyan"
              title="Refresh stats"
            >
              <RefreshCw size={14} className={isFetching ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="mx-auto max-w-4xl space-y-4">
          {isLoading && !data ? (
            <div className="py-8 text-center text-cyber-muted">
              <span className="animate-spin-glow mr-2 inline-block">&#9680;</span>
              Loading usage stats...
            </div>
          ) : !data || data.totals.responses === 0 ? (
            <div className="py-14 text-center">
              <BarChart3 size={32} className="mx-auto mb-3 text-cyber-muted/30" />
              <p className="mb-1 text-sm text-cyber-muted">No usage data yet</p>
              <p className="text-xs text-cyber-muted/60">
                Run some prompts, then stats will appear here.
              </p>
            </div>
          ) : tab === "overview" ? (
            <StatsOverview data={data} />
          ) : (
            <StatsAdvanced data={data} />
          )}
        </div>
      </div>
    </div>
  );
}
